import { microsoftConsumerSecurityClient } from 'client-factories/MicrosoftConsumerSecurityClientFactoryV1';
import { isItmE2EEncryptionEnabled, isItmE2EEncryptionEnabledOnMac } from 'common/utilities/FlightSetting';
import TelemetryProvider from 'common/utilities/TelemetryProvider';
import { Crypto } from 'cryptoSdk/src/Crypto';
import { GetEncryptionAuthTokenApiResponse } from 'mcsclient/models';

function createKeyRequestServicePayloadXml(partnerNumber: string) {
  return `
    <CSIAPIRequest>
        <OfficeData>
            <Service>KeyRequestService</Service>
            <Version>2.0</Version>
        </OfficeData>
        <SubscriberData>
            <PartnerNumber>${partnerNumber}</PartnerNumber>
        </SubscriberData>
    </CSIAPIRequest>
  `;
}

export async function fetchOauthToken(): Promise<GetEncryptionAuthTokenApiResponse> {
  try {
    const response = await microsoftConsumerSecurityClient.identityTheftMonitoringSubscription.getEncryptionAuthToken();
    return response;
  } catch (e) {
    TelemetryProvider.Instance.trackUnknownException(e, 'fetchExperianOauthTokenFailed');
    throw e;
  }
}

export async function fetchEncryptionKey(
  oauthToken: GetEncryptionAuthTokenApiResponse
): Promise<{ key?: string; cipherTextBlob?: string; encrypter?: Crypto | undefined }> {
  let responseText = '';

  try {
    const { baseUri, accessToken, partnerNumber } = oauthToken;
    const response = await fetch(baseUri, {
      method: 'POST',
      body: createKeyRequestServicePayloadXml(partnerNumber),
      headers: {
        AuthorizationType: 'eps-authorizer',
        Authorization: `Bearer ${accessToken}`,
      },
    });
    responseText = await response.text();

    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(responseText, 'text/xml');
    const errorText = xmlDoc.getElementsByTagName('ErrorText')[0]?.childNodes[0]?.nodeValue || '';
    if (errorText.trim()) {
      throw new Error(errorText);
    } else {
      const key = xmlDoc.getElementsByTagName('Key')[0].childNodes[0].nodeValue || undefined;
      const cipherTextBlob = xmlDoc.getElementsByTagName('CipherTextBlob')[0].childNodes[0].nodeValue || undefined;

      return {
        key,
        cipherTextBlob,
        encrypter: key ? Crypto.initialize('', key) : undefined,
      };
    }
  } catch (e) {
    TelemetryProvider.Instance.trackUnknownException(e, 'fetchExperianEncryptionKeysFailed');
    throw e;
  }
}

export async function fetchE2EEncryptionKeys(
  isMacOsWebView: boolean
): Promise<{ key?: string; cipherTextBlob?: string; encrypter?: Crypto | undefined }> {
  const noKeyResponse = { key: undefined, cipherTextBlob: undefined, encrypter: undefined };

  if (isItmE2EEncryptionEnabled() && (!isMacOsWebView || isItmE2EEncryptionEnabledOnMac())) {
    try {
      const oauthTokenResponse = await fetchOauthToken();
      const encryptionData = await fetchEncryptionKey(oauthTokenResponse);
      return encryptionData;
    } catch (e) {
      return noKeyResponse;
    }
  }

  return noKeyResponse;
}

export async function encryptDataValue(value: string | undefined, encrypter?: Crypto) {
  if (encrypter && value) {
    return encrypter.encrypt(value);
  }
  return value;
}

export async function encryptDataValues(values: (string | undefined)[], encrypter?: Crypto) {
  return Promise.all(values.map((value) => encryptDataValue(value, encrypter)));
}
